import { takeLatest, call, put } from 'redux-saga/effects';
import { AIApi } from '../api/AIApi';
import {
  FETCH_INSIGHTS,
  fetchInsightsSuccess,
  fetchInsightsError
} from '../actions/insights';
import { QUESTION_TYPES } from '../api/constants';

// Bind API methods to ensure proper this context
const boundAIApi = {
  getInsights: AIApi.getInsights.bind(AIApi),
  getEventDetails: AIApi.getEventDetails.bind(AIApi),
  getEventPosts: AIApi.getEventPosts.bind(AIApi),
  getEventStats: AIApi.getEventStats.bind(AIApi)
};

// Map event question types to our internal types
const mapQuestionType = (type) => {
  if (!type) return QUESTION_TYPES.OPEN_ENDED;
  
  // Convert to uppercase for comparison
  const upperType = type.toUpperCase();
  
  // Direct match with our types
  if (Object.values(QUESTION_TYPES).includes(upperType)) {
    return upperType;
  }
  
  // Map common variations
  const typeMap = {
    'scale_1_5': QUESTION_TYPES.SCALE_5,
    'scale_1_10': QUESTION_TYPES.SCALE_10,
    'yes_no': QUESTION_TYPES.BINARY,
    'multiple': QUESTION_TYPES.MULTIPLE_CHOICE,
    'open': QUESTION_TYPES.OPEN_ENDED,
    'default': QUESTION_TYPES.OPEN_ENDED
  };
  
  return typeMap[type.toLowerCase()] || QUESTION_TYPES.OPEN_ENDED;
};

function* fetchInsights(action) {
  // Extract eventId and questionType from payload
  const eventId = action.payload?.eventId;
  const questionType = action.payload?.questionType || 'default';
  
  if (!eventId) {
    console.error('Saga: Missing eventId in payload', { payload: action.payload });
    yield put(fetchInsightsError(null, new Error('Missing eventId in payload')));
    return;
  }

  try {
    // Map the question type to our internal types
    const mappedQuestionType = mapQuestionType(questionType);
    
    // console.log('Saga: Starting fetch insights', { 
    //   eventId,
    //   originalType: questionType,
    //   mappedType: mappedQuestionType,
    //   payload: action.payload,
    //   timestamp: new Date().toISOString()
    // });
    
    const response = yield call(boundAIApi.getInsights, eventId, { 
      questionType: mappedQuestionType,
      model: 'gpt-4'
    });
    
    // Add log to check the raw response
    // console.log('API Response:', response);

    // Log raw prompt and response for debugging
    // console.log('🔍 Debug - Raw Prompt:', {
    //   eventId,
    //   questionType: mappedQuestionType,
    //   prompt: response?.debugInfo?.prompt,
    //   timestamp: new Date().toISOString()
    // });

    // console.log('🤖 Debug - LLM Response:', {
    //   eventId,
    //   questionType: mappedQuestionType,
    //   rawResponse: response?.debugInfo?.response?.raw,
    //   parsedContent: response?.content,
    //   timestamp: new Date().toISOString()
    // });
    
    // console.log('Saga: Fetch successful', {
    //   eventId,
    //   questionType: mappedQuestionType,
    //   hasResponse: !!response,
    //   hasContent: !!response?.content,
    //   fromCache: !!response?.debugInfo?.fromCache,
    //   timestamp: new Date().toISOString()
    // });
    
    if (!response?.content) {
      throw new Error('Invalid insights format: missing content');
    }
    
    // Normalize fromCache property for clarity
    const fromCache = !!response.fromCache || !!response.debugInfo?.fromCache;
    const responseWithQuestionType = {
      ...response,
      questionType: mappedQuestionType,
      fromCache
    };
    console.debug('[AIInsights][Saga] Forwarding insights with fromCache:', fromCache, { eventId, questionType: mappedQuestionType });
    yield put(fetchInsightsSuccess(eventId, responseWithQuestionType));
  } catch (error) {
    console.error('Saga: Fetch failed', {
      eventId,
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
        details: error.details
      },
      timestamp: new Date().toISOString()
    });
    
    yield put(fetchInsightsError(eventId, error));
  }
}

export function* insightsSaga() {
  yield takeLatest(FETCH_INSIGHTS, fetchInsights);
} 